import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import EquationInput from '../../../../components/inputs/EquationInput';
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";


/*
  Zadanie maturalne – otwarte (4 pkt)

  Prostopadłościan, ciąg geometryczny krawędzi, pole całkowite -> objętość.
*/

// ===== pomocnicze =====

function normalizeLatex(raw) {
  if (raw == null) return '';
  if (typeof raw === 'object' && raw.raw != null) {
    return String(raw.raw).replace(/\s+/g, '');
  }
  return String(raw).replace(/\s+/g, '');
}

// Normalizacja liczbowo-tekstowa (przecinki, {,}, \cdot itp.)
function normalizeMathString(raw) {
  return normalizeLatex(raw)
    .replaceAll('{,}', '.')
    .replaceAll(',', '.')
    .replaceAll('\\cdot', '')
    .replaceAll('·', '')
    .replaceAll(' ', '');
}

function parseLatexNumber(raw) {
  const s0 = normalizeLatex(raw);
  if (!s0) return null;
  const s = s0.replace(',', '.');

  const fracMatch = s.match(/^(-?)\\frac\{(-?\d+)\}\{(\d+)\}$/);
  if (fracMatch) {
    const sign = fracMatch[1] === '-' ? -1 : 1;
    const num = parseFloat(fracMatch[2]);
    const den = parseFloat(fracMatch[3]);
    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
    return (sign * num) / den;
  }

  const num = Number(s);
  if (!Number.isFinite(num)) return null;
  return num;
}

// Prosty parser: "312/78" (bez nawiasów, bez potęg)
function parseSimpleDivisionNumber(raw) {
  const s = normalizeMathString(raw);
  if (!s) return null;

  // Najpierw spróbuj zwykłej liczby
  const direct = Number(s);
  if (Number.isFinite(direct)) return direct;

  // Potem spróbuj -?\d+/\d+
  const m = s.match(/^(-?\d+)\/(\d+)$/);
  if (!m) return null;
  const num = Number(m[1]);
  const den = Number(m[2]);
  if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
  return num / den;
}

// Jednomian typu: A, a, 3A, -2a, 12x
function parseMonomialCoef(raw) {
  const s = normalizeMathString(raw);
  if (!s) return null;

  // np: a, A, 3a, -2A
  const m1 = s.match(/^(-?\d+)?([A-Za-z])$/);
  if (m1) {
    const coef = m1[1] == null || m1[1] === '' ? 1 : Number(m1[1]);
    if (!Number.isFinite(coef)) return null;
    return { coef, sym: m1[2] };
  }

  // np: 3*a (jeśli ktoś wpisze gwiazdkę)
  const m2 = s.match(/^(-?\d+)\*([A-Za-z])$/);
  if (m2) {
    const coef = Number(m2[1]);
    if (!Number.isFinite(coef)) return null;
    return { coef, sym: m2[2] };
  }

  return null;
}

function approxEqual(x, y, eps = 1e-6) {
  if (x == null || y == null) return false;
  return Math.abs(x - y) <= eps;
}

function isA2Token(s) {
  // case-insensitive: a^2 / A^2
  return s === 'a^2' || s === 'a^{2}' || s === 'A^2' || s === 'A^{2}';
}
function isAToken(s) {
  return s === 'a' || s === 'A';
}

// Próba odczytu równania "lewa= prawa" i sprawdzenie wersji zredukowanej:
// a^2 = liczba   lub   a = liczba (po normalizacji)
function checkReducedEquation(eqRaw, aNumeric, aSquaredNumeric) {
  const s = normalizeMathString(eqRaw);
  if (!s || !s.includes('=')) return false;

  const parts = s.split('=');
  if (parts.length !== 2) return false;

  const L = parts[0];
  const R = parts[1];

  // a^2 = ...
  if (isA2Token(L) || isA2Token(R)) {
    const other = isA2Token(L) ? R : L;
    const val = parseSimpleDivisionNumber(other);
    if (val == null) return false;
    return approxEqual(val, aSquaredNumeric);
  }

  // a = ...
  if (isAToken(L) || isAToken(R)) {
    const other = isAToken(L) ? R : L;
    const val = parseSimpleDivisionNumber(other);
    if (val == null) return false;
    return approxEqual(val, aNumeric);
  }

  return false;
}

// Minimalny formatter do podglądu (żeby mieć ładne potęgi i ułamki liczbowe)
// — wystarcza dla: a^2=4, 27a^3, 312/78 itd.
function toPreviewLatex(raw) {
  let s = String(raw ?? '').trim();
  if (!s) return '';

  // usuń spacje i mnożenie "·" "*"
  s = s.replace(/\s+/g, '').replace(/(\*|·)/g, '');

  // zamień wielocyfrowe wykładniki: x^12 -> x^{12}, x^-12 -> x^{-12}
  s = s.replace(/\^(-?\d{2,})/g, '^{$1}');

  // najczęstsze: a^2 / a^3 (także wielka A)
  s = s.replace(/([aA])\^2/g, '$1^{2}');
  s = s.replace(/([aA])\^3/g, '$1^{3}');

  // proste ułamki liczbowe: 312/78 -> \dfrac{312}{78}
  s = s.replace(/(?<!\\dfrac\{)(-?\d+)\/(\d+)/g, (_, n, d) => `\\dfrac{${n}}{${d}}`);

  return s;
}

function renderDynamicPreview(prefixLatex, value) {
  const v = normalizeLatex(value);
  if (!v) return null;

  const body = toPreviewLatex(v);
  if (!body) return null;

  return (
    <div style={{ marginTop: 2 }}>
      <strong>Podgląd:</strong>{' '}
      <InlineMath math={`${prefixLatex}${body}`} />
    </div>
  );
}

// ilorazy ciągu i możliwe długości najkrótszej krawędzi (bez 1)
const RATIOS = [2, 3, 4, 5];
// wykluczamy 1, żeby zadanie nie było zbyt proste
const A_VALUES = [2, 3, 4];

const pickOne = (arr) => arr[Math.floor(Math.random() * arr.length)];

function numberToCommaLatex(n) {
  if (Number.isInteger(n)) return String(n);
  const s = String(n);
  const [intPart, fracPart] = s.split('.');
  if (!fracPart) return s;
  return `${intPart}{,}${fracPart}`;
}

// ===== generator =====

function generateJune2025Original() {
  return {
    type: 'MaturaCuboidGeoSequenceVolumeOpenTask',
    data: {
      maxPoints: 4,
      surfaceLatex: '94{,}5',
      surfaceNumeric: 94.5,
      ratio: 4,
      k1: 1,
      k2: 4,
      k3: 16,
      aNumeric: 0.75,
      aLatex: '0{,}75',
      aSquaredNumeric: 0.5625,
      pcCoefNumeric: 168,
      volumeFormulaLatex: '64a^{3}',
      volumeNumeric: 27,
      volumeLatex: '27',
      equationLatex: '94{,}5 = 168a^{2}',
    },
    source: {
      variant: 'original',
      exam: 'MATURA',
      session: '2025_CZE',
      taskNumber: 24,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === 'original' &&
    generationOptions.session === '2025_CZE'
  ) {
    return generateJune2025Original();
  }

  const ratio = pickOne(RATIOS); // q
  const aNumeric = pickOne(A_VALUES); // a ∈ {2,3,4}

  const k1 = 1;
  const k2 = ratio;
  const k3 = ratio * ratio;

  const sumR = ratio + ratio * ratio + ratio * ratio * ratio;
  const pcCoefNumeric = 2 * sumR; // współczynnik przy a^2 w Pc

  const aSquaredNumeric = aNumeric * aNumeric;

  const surfaceNumeric = pcCoefNumeric * aSquaredNumeric;
  const surfaceLatex = numberToCommaLatex(surfaceNumeric);

  const volumeCoefNumeric = ratio * ratio * ratio; // q^3
  const volumeNumeric = volumeCoefNumeric * aNumeric * aNumeric * aNumeric;

  const aLatex = String(aNumeric);
  const volumeFormulaLatex = `${volumeCoefNumeric}a^{3}`;
  const volumeLatex = numberToCommaLatex(volumeNumeric);
  const equationLatex = `${surfaceLatex} = ${pcCoefNumeric}a^{2}`;

  const data = {
    maxPoints: 4,
    surfaceLatex,
    surfaceNumeric,
    ratio,
    k1,
    k2,
    k3,
    aNumeric,
    aLatex,
    aSquaredNumeric,
    pcCoefNumeric,
    volumeFormulaLatex,
    volumeNumeric,
    volumeLatex,
    equationLatex,
  };

  return {
    type: 'MaturaCuboidGeoSequenceVolumeOpenTask',
    data,
  };
}

// ===== treść zadania =====

export function renderTask({ data }) {
  const { surfaceLatex, ratio } = data;

  return (
    <div className="space-y-3" style={{ textAlign: 'left' }}>
      <p>
        <strong>Zadanie maturalne – otwarte (0–4 pkt).</strong>
      </p>

      <p>
        Pole powierzchni całkowitej prostopadłościanu jest równe{' '}
        <InlineMath math={surfaceLatex} />. Długości trzech krawędzi
        wychodzących z tego samego wierzchołka tego prostopadłościanu tworzą
        ciąg geometryczny o ilorazie równym{' '}
        <InlineMath math={String(ratio)} />.
      </p>

      <p>Oblicz objętość tego prostopadłościanu. Zapisz obliczenia.</p>
    </div>
  );
}

// ===== input / slots =====
//
// 1) współczynniki ciągu
// 2) równanie Pc lub V(a)
// 3) najkrótsza krawędź
// 4) objętość

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};
  const setField =
    (key) =>
    (val) =>
      onChange({ ...(ua || {}), [key]: val });

  return (
    <div style={{ display: 'grid', rowGap: 18, maxWidth: 520 }}>
      <OpenTaskScoringHint variant="independent" />
      {/* 1. współczynniki ciągu geometrycznego */}
      <div style={{ display: 'grid', rowGap: 6 }}>
        <div style={{ fontSize: 16 }}>
          Współczynniki stosunku długości trzech krawędzi prostopadłościanu
          wychodzących z jednego wierzchołka:
        </div>

        <div style={{ display: 'grid', rowGap: 8 }}>
          <div>
            <EquationInput
              value={ua.k1}
              onChange={setField('k1')}
              disabled={disabled}
              placeholder="np. 1"
              ariaLabel="pierwszy współczynnik"
              showPreview={false}
            />
            {renderDynamicPreview('k_1=', ua.k1)}
          </div>

          <div>
            <EquationInput
              value={ua.k2}
              onChange={setField('k2')}
              disabled={disabled}
              placeholder="np. 3"
              ariaLabel="drugi współczynnik"
              showPreview={false}
            />
            {renderDynamicPreview('k_2=', ua.k2)}
          </div>

          <div>
            <EquationInput
              value={ua.k3}
              onChange={setField('k3')}
              disabled={disabled}
              placeholder="np. 9"
              ariaLabel="trzeci współczynnik"
              showPreview={false}
            />
            {renderDynamicPreview('k_3=', ua.k3)}
          </div>
        </div>
      </div>

      {/* 2. równanie LUB V(a) */}
      <div style={{ display: 'grid', rowGap: 6 }}>
        <div style={{ fontSize: 16 }}>
          Zapisz <strong>równanie z jedną niewiadomą</strong> dla pola
          powierzchni całkowitej <InlineMath math={'P_c'} />{' '}
          <strong>lub</strong> objętość w zależności od{' '}
          <InlineMath math={'a'} />.
        </div>

        <div style={{ display: 'grid', rowGap: 4 }}>
          <div>Równanie na pole całkowite (może być też po redukcji):</div>

          <div>
            <EquationInput
              value={ua.eq}
              onChange={setField('eq')}
              disabled={disabled}
              placeholder="np. 312=78a^2"
              ariaLabel="równanie z jedną niewiadomą"
              showPreview={false}
            />
            {renderDynamicPreview('', ua.eq)}
          </div>
        </div>

        <div style={{ display: 'grid', rowGap: 4, marginTop: 4 }}>
          <div>
            Objętość w zależności od <InlineMath math={'a'} />:
          </div>

          <div>
            <EquationInput
              value={ua.vOfA}
              onChange={setField('vOfA')}
              disabled={disabled}
              placeholder="np. 27a^3"
              ariaLabel="objętość w zależności od a"
              showPreview={false}
            />
            {/* Podgląd zawsze jako V(a)=..., nawet jeśli user wpisał V=... */}
            {renderDynamicPreview('V(a)=', ua.vOfA)}
          </div>
        </div>
      </div>

      {/* 3. najkrótsza krawędź */}
      <div style={{ display: 'grid', rowGap: 4 }}>
        <div style={{ fontSize: 16 }}>
          Długość najkrótszej krawędzi prostopadłościanu:{' '}
          <InlineMath math={'a ='} />
        </div>

        <div>
          <EquationInput
            value={ua.a}
            onChange={setField('a')}
            disabled={disabled}
            placeholder="np. 2"
            ariaLabel="długość najkrótszej krawędzi"
            showPreview={false}
          />
          {renderDynamicPreview('a=', ua.a)}
        </div>
      </div>

      {/* 4. objętość */}
      <div style={{ display: 'grid', rowGap: 4 }}>
        <div style={{ fontSize: 16 }}>
          Objętość prostopadłościanu:{' '}
          <InlineMath math={'V ='} />
        </div>

        <div>
          <EquationInput
            value={ua.V}
            onChange={setField('V')}
            disabled={disabled}
            placeholder="np. 216 (może być też V=216)"
            ariaLabel="objętość prostopadłościanu"
            showPreview={false}
          />
          {renderDynamicPreview('V=', ua.V)}
        </div>
      </div>
    </div>
  );
}

// ===== sprawdzanie punktów (0–4) =====

export function checkAnswer({ data }, userAnswer) {
  const ua = userAnswer || {};
  const {
    ratio,
    aNumeric,
    aSquaredNumeric,
    surfaceNumeric,
    pcCoefNumeric,
    volumeNumeric,
  } = data;

  // 1) współczynniki – liczy się stosunek, dopuszczamy skalowanie,
  //    a także zapis typu x, 3x, 9x.
  const u1 = parseLatexNumber(ua.k1);
  const u2 = parseLatexNumber(ua.k2);
  const u3 = parseLatexNumber(ua.k3);

  let ratioOk = false;

  // (a) liczby
  if (u1 != null && u2 != null && u3 != null && u1 !== 0 && u2 !== 0) {
    const r1 = u2 / u1;
    const r2 = u3 / u2;
    ratioOk = approxEqual(r1, ratio) && approxEqual(r2, ratio);
  } else {
    // (b) jednomiany: x, 3x, 9x
    const m1 = parseMonomialCoef(ua.k1);
    const m2 = parseMonomialCoef(ua.k2);
    const m3 = parseMonomialCoef(ua.k3);

    if (
      m1 &&
      m2 &&
      m3 &&
      m1.coef !== 0 &&
      m2.coef !== 0 &&
      m1.sym === m2.sym &&
      m2.sym === m3.sym
    ) {
      const r1 = m2.coef / m1.coef;
      const r2 = m3.coef / m2.coef;
      ratioOk = approxEqual(r1, ratio) && approxEqual(r2, ratio);
    }
  }

  // 2) równanie lub V(a)
  const eqRaw = normalizeMathString(ua.eq);
  const eqRawLower = eqRaw.toLowerCase();

  const vOfARaw = normalizeMathString(ua.vOfA);
  const vOfARawLower = vOfARaw.toLowerCase();

  // (2a) „pełna” postać: zawiera oba numery i a^2 + znak równości
  const surfStr = String(surfaceNumeric);
  const coefStr = String(pcCoefNumeric);

  const hasEqSign = eqRawLower.includes('=');
  const hasA2 =
    eqRawLower.includes('a^2') || eqRawLower.includes('a^{2}');
  const hasSurf = eqRawLower.includes(surfStr);
  const hasCoef = eqRawLower.includes(coefStr);

  const eqFullOk = hasEqSign && hasA2 && hasSurf && hasCoef;

  // (2b) postać po redukcji: a^2 = a^2num lub a = anum lub a^2 = surf/coef
  const eqReducedOk = checkReducedEquation(eqRaw, aNumeric, aSquaredNumeric);

  const eqOk = eqFullOk || eqReducedOk;

  // (2c) V(a): dopuszczamy też zapis z "V=" lub "V(a)=" na początku
  const vCoefStr = String(ratio * ratio * ratio);
  const hasA3 =
    vOfARawLower.includes('a^3') || vOfARawLower.includes('a^{3}');
  const hasVCoef = vOfARawLower.includes(vCoefStr);
  const vOfAOk = hasA3 && hasVCoef;

  const midOk = eqOk || vOfAOk;

  // 3) najkrótsza krawędź
  const aUser = parseLatexNumber(ua.a);
  const aOk = approxEqual(aUser, aNumeric);

  // 4) objętość (tu też tolerujemy "V=216" – wyciągamy liczbę, jeśli da się)
  //    parseLatexNumber nie przejdzie dla "V=216", więc robimy fallback:
  let VUser = parseLatexNumber(ua.V);
  if (VUser == null) {
    const s = normalizeMathString(ua.V);
    // wyciągnij końcową liczbę, jeśli ktoś wpisał np. "V=216"
    const m = s.match(/(-?\d+(?:\.\d+)?)$/);
    if (m) {
      const n = Number(m[1]);
      if (Number.isFinite(n)) VUser = n;
    }
  }
  const VOk = approxEqual(VUser, volumeNumeric);

  let score = 0;
  if (ratioOk) score += 1;
  if (midOk) score += 1;
  if (aOk) score += 1;
  if (VOk) score += 1;

  const parts = {
    ratios: ratioOk,
    equationOrVofA: midOk,
    shortestEdge: aOk,
    volume: VOk,
  };

  return {
    isCorrect: score === 4,
    score,
    maxScore: 4,
    parts,
    details: {
      ratioOk,
      midOk,
      aOk,
      VOk,
      eqOk,
      eqFullOk,
      eqReducedOk,
      vOfAOk,
    },
  };
}

// ===== poprawna odpowiedź =====

export function renderAnswer({ data }) {
  const { k1, k2, k3, aLatex, equationLatex, volumeFormulaLatex, volumeLatex } =
    data;

  return (
    <div style={{ marginTop: 8, display: 'grid', rowGap: 4 }}>
      <div>
        Proporcje krawędzi:{' '}
        <InlineMath math={`${k1},\\;${k2},\\;${k3}`} />
      </div>
      <InlineMath math={equationLatex} />
      <InlineMath math={`V = ${volumeFormulaLatex}`} />
      <InlineMath math={`a = ${aLatex}`} />
      <InlineMath math={`V = ${volumeLatex}`} />
    </div>
  );
}

// ===== odpowiedź użytkownika =====

export function renderUserAnswer(userAnswer) {
  if (!userAnswer) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { k1, k2, k3, eq, vOfA, a, V } = userAnswer;

  const latexOrDash = (field) => {
    const s = normalizeLatex(field);
    return s ? s : '\\text{–}';
  };

  return (
    <div style={{ marginTop: 8, display: 'grid', rowGap: 4 }}>
      <InlineMath
        math={`k_1 = ${latexOrDash(k1)},\\;k_2 = ${latexOrDash(
          k2
        )},\\;k_3 = ${latexOrDash(k3)}`}
      />
      <InlineMath math={`\\text{równanie: } ${latexOrDash(eq)}`} />
      <InlineMath math={`V(a) = ${latexOrDash(vOfA)}`} />
      <InlineMath math={`a = ${latexOrDash(a)}`} />
      <InlineMath math={`V = ${latexOrDash(V)}`} />
    </div>
  );
}

// ===== wyjaśnienie krok po kroku =====

export function renderExplanation({ data }) {
  const {
    surfaceLatex,
    ratio,
    k1,
    k2,
    k3,
    aLatex,
    aSquaredNumeric,
    pcCoefNumeric,
    volumeFormulaLatex,
    volumeLatex,
  } = data;

  const r = ratio;
  const r2 = ratio * ratio;

  return (
    <div className="space-y-3" style={{ textAlign: 'left' }}>
      <p>
        <strong>Krok 1.</strong> Krawędzie wychodzące z jednego wierzchołka
        tworzą ciąg geometryczny o ilorazie{' '}
        <InlineMath math={String(ratio)} />. Oznaczmy długość najkrótszej
        krawędzi przez <InlineMath math={'a'} />. Wtedy możemy przyjąć
        długości:
      </p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={`a,\\;${r}a,\\;${r2}a.`} />
      </div>

      <p>
        Współczynniki są więc proporcjonalne do{' '}
        <InlineMath math={`${k1},\\;${k2},\\;${k3}`} />.
      </p>

      <p>
        <strong>Krok 2.</strong> Pole powierzchni całkowitej prostopadłościanu
        o krawędziach <InlineMath math={`a,\\;b=${r}a,\\;c=${r2}a`} /> jest równe:
      </p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={'P_c = 2(ab + bc + ac).'} />
      </div>

      <div style={{ textAlign: 'center' }}>
        <BlockMath
          math={`\\begin{aligned}P_c &= 2\\left(${r}+${r2}+${r * r2}\\right)a^{2}\\\\ &= ${pcCoefNumeric}a^{2}.\\end{aligned}`}
        />
      </div>

      <p>
        Z warunku <InlineMath math={`P_c = ${surfaceLatex}`} /> otrzymujemy
        równanie:
      </p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={`${surfaceLatex} = ${pcCoefNumeric}a^{2}.`} />
      </div>

      <div style={{ textAlign: 'center' }}>
        <BlockMath
          math={`a^{2} = \\dfrac{${surfaceLatex}}{${pcCoefNumeric}} = ${numberToCommaLatex(aSquaredNumeric)}.`}
        />
      </div>

      <p>Jako długość krawędzi <InlineMath math="a" /> jest dodatnia, więc:</p>
      <div style={{ textAlign: 'center' }}>
        <BlockMath math={`a = ${aLatex}.`} />
      </div>

      <p>
        <strong>Krok 3.</strong> Objętość prostopadłościanu to iloczyn długości
        trzech krawędzi:
      </p>

      <div style={{ textAlign: 'center' }}>
        <BlockMath
          math={`V = a \\cdot ${r}a \\cdot ${r2}a = ${volumeFormulaLatex}.`}
        />
      </div>

      <div style={{ textAlign: 'center' }}>
        <BlockMath math={`V = ${volumeLatex}.`} />
      </div>

      <p>
        Dla wylosowanych danych{' '}
        <InlineMath math={`a = ${aLatex}`} /> otrzymujemy{' '}
        <InlineMath math={`V = ${volumeLatex}`} />.
      </p>
    </div>
  );
}
